import React, { useState, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppNavbar from '../Components/AppNavbar';
import {
  signUp,
  signIn,
  confirmSignUp,
  resendSignUpCode,
  signInWithRedirect,
  fetchAuthSession,
  getCurrentUser,
  signOut
} from "aws-amplify/auth";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  Button,
  InputOtp,
  Tabs,
  Tab,
  Divider
} from "@heroui/react";
import TurnstileBox from "../Components/TurnstileBox";
import { BackendContext } from "../Context/BackendContext";

const GoogleIcon = (props) => (
  <svg
    viewBox="0 0 533.5 544.3"
    width="18"
    height="18"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path fill="#4285F4" d="M533.5 278.4c0-17.4-1.6-34.1-4.6-50.4H272v95.4h146.9c-6.3 34-25.1 62.8-53.5 82v68h86.5c50.7-46.7 81.6-115.4 81.6-195z"/>
    <path fill="#34A853" d="M272 544.3c72.6 0 133.6-24 178.1-65.3l-86.5-68c-24.1 16.2-55 25.7-91.6 25.7-70.3 0-129.9-47.4-151.3-111.2H31.8v69.7C76 480.7 168.7 544.3 272 544.3z"/>
    <path fill="#FBBC05" d="M120.7 325.5c-4.9-14.7-7.7-30.5-7.7-46.6s2.8-31.8 7.7-46.6v-69.7H31.8C11.5 196.5 0 239.3 0 278.9s11.5 82.4 31.8 115.8l88.9-69.2z"/>
    <path fill="#EA4335" d="M272 109.4c39.5 0 75 13.6 103 40.5l77-77.1C405.5 26.3 344.5 0 272 0 168.7 0 76 63.6 31.8 163.1l88.9 69.2C142.1 156.8 201.7 109.4 272 109.4z"/>
  </svg>
);

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const AuthPage = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("signIn");
  const [formData, setFormData] = useState({
    username: "",
    preferredUsername: "",
    password: "",
    confirmationCode: "",
  });
  const [message, setMessage] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [tsToken, setTsToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const { callHumanVerify } = useContext(BackendContext);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogle = async () => {
    // This sends the user to Cognito Hosted UI → Google
    await signInWithRedirect({ provider: 'Google' });
  };

  // ✅ keep function identity stable
  const handleToken = useCallback((t) => setTsToken(t), []);

  // ✅ memoize options so they don't change each render
  const turnstileOptions = useMemo(() => ({
    action: "signup",
    appearance: "always",
    // theme: "auto", // default
  }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setPopUpMessage("");
    setError("");

    const form = new FormData(e.target);
    const submittedUsername = form.get("username")?.trim();

    if (!submittedUsername) {
      setError("Email is required.");
      return;
    }

    //console.log("📩 Username from form:", submittedUsername);
    // update formData.username to be safe
    setFormData((prev) => ({ ...prev, username: submittedUsername }));

    try {
      if (selectedTab === "signUp") {
        if (!SITE_KEY) throw new Error("Turnstile site key is missing.");
        if (!tsToken)  throw new Error("Please complete the human verification.");

        setLoading(true);

        const verify = await callHumanVerify(tsToken);
        if (!verify?.ok) {
          const reason = (verify?.errorCodes || []).join(", ") || "Unknown";
          throw new Error(`Human verification failed: ${reason}`);
        }

        await signUp({
          username: submittedUsername,
          password: formData.password,
          autoSignIn: { enabled: true },
          options: {
            clientMetadata: { tsToken }, // <= important
          },
        });

        setIsModalOpen(true);
        setMessage("Signup successful! Enter the confirmation code sent to your email.");
      } else {
        const response = await signIn({
          username: submittedUsername,
          password: formData.password,
        });

        if (
          response?.nextStep?.signInStep === "CONFIRM_SIGN_UP" ||
          response?.isSignedIn === false
        ) {
          setFormData((prev) => ({ ...prev, username: submittedUsername }));
          await handleResendCode(submittedUsername);
          setMessage("Account not confirmed. A new code has been sent.");
          setIsModalOpen(true);
          return;
        }

        setMessage("Sign in successful!");

        setTimeout(() => {
          //window.location.reload();
          navigate('/');
        }, 200);
      }
    } catch (err) {
      if (err.name === "UsernameExistsException") {
        setMessage("This email is linked to another account. Please sign in.");
      } else {
        setError(err.message || "Error occurred.");
      }
    }
  };


  const handleConfirm = async () => {
    try {
      if (!formData.username) {
        setError("Username is missing. Please sign up again.");
        return;
      }
      await confirmSignUp({
        username: formData.username,
        confirmationCode: formData.confirmationCode,
      });
      setPopUpMessage("Confirmation successful! You can now sign in.");
      setSelectedTab("signIn");
      setIsModalOpen(false);
    } catch (err) {
      setError(err.message || "Error confirming sign-up.");
    }
  };

  const handleResendCode = async (username) => {
    if (!username) {
      console.error("❌ No username passed to handleResendCode");
      setError("Missing email to resend confirmation code.");
      return;
    }

    try {
      //console.log("📤 Resending code for:", username);
      await resendSignUpCode({
        username
      });
      setMessage("A new confirmation code has been sent to your email.");
      setPopUpMessage("A new confirmation code has been sent to your email.");
      setIsModalOpen(true);
    } catch (err) {
      console.error("Resend code error:", err);
      setError(err.message || "Error resending confirmation code.");
    }
  };

  return (
    <div className="h-screen w-screen bg-slate-900 text-white">
      <AppNavbar />
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] px-4">
        <div className="bg-slate-800 border border-zinc-700 p-8 rounded-2xl shadow-lg w-full max-w-md">
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(key)}
            className="mb-6"
            variant="underlined"
            classNames={{
              tabList: "gap-6",
              tab: "text-white data-[selected=true]:text-blue-500",
            }}
          >
            <Tab key="signIn" title="Sign In" />
            <Tab key="signUp" title="Sign Up" />
          </Tabs>

          {message && <p className="text-green-400 mb-3 text-center">{message}</p>}
          {error && <p className="text-red-500 mb-3 text-center">{error}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedTab === "signIn" && (
              <>
              <Button
                type="button"
                onPress={handleGoogle}
                fullWidth
                startContent={<GoogleIcon />}
                variant="flat"
                className="w-full bg-white text-slate-900 shadow-sm
                          hover:shadow-md active:shadow
                          dark:bg-white dark:text-slate-900"
                classNames={{
                  base: "border border-slate-200 dark:border-slate-300 rounded-xl h-11",
                  content: "font-medium",
                  startContent: "mr-2",
                }}
              >
                Continue with Google
              </Button>
              <div className="flex items-center px-4 space-x-4">
                <Divider className="h-px flex-1 bg-gray-700"/>
                <span className="text-s text-gray-400">or</span>
                <Divider className="h-px flex-1 bg-gray-700"/>
              </div>
              </>
            )}
            <Input
              type="email"
              name="username"
              label="Email"
              placeholder="Enter your email"
              onChange={handleChange}
              isRequired
              classNames={{
                inputWrapper: "bg-gray-900 text-white"
              }}
            />
            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="Enter your password"
              onChange={handleChange}
              isRequired
              classNames={{
                inputWrapper: "bg-gray-900 text-white"
              }}
            />
            <>
              <TurnstileBox
                siteKey={SITE_KEY}
                onToken={handleToken}
                options={turnstileOptions}
                className="my-2"
              />
              {!SITE_KEY && (
                <p className="text-amber-400 text-xs">
                  Missing REACT_APP_TURNSTILE_SITE_KEY in .env.local (root). Restart dev server.
                </p>
              )}
            </>
            <Button disabled={!tsToken} type="submit" color="primary" 
              className="w-full bg-indigo-600 hover:bg-indigo-700">
              {selectedTab === "signUp" ? "Create Account" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="bg-gray-800 text-white">
        <ModalContent>
          <ModalHeader>Confirm Your Email</ModalHeader>
          <ModalBody>
            {popUpMessage && <p className="text-green-500 text-sm">{popUpMessage}</p>}
            <InputOtp
              classNames={{
                segment: "bg-gray-700 text-white mt-4",
                input: "text-white",
              }}
              length={6}
              name="confirmationCode"
              onValueChange={(val) =>
                setFormData((prev) => ({ ...prev, confirmationCode: val }))
              }
              isRequired
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={handleConfirm}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AuthPage;
